import {
  Accordion, AccordionHeader, AccordionItem, AccordionPanel, Badge, MessageBar, MessageBarBody,
  Text, makeStyles, tokens,
} from '@fluentui/react-components';
import type { RoutingDecision } from '../../api/routingDecisionsClient.ts';
import { attemptKindLabel, candidateExplanation, outcomeExplanation } from './routingDecisionPresentation.ts';

const useStyles = makeStyles({
  heading: { margin: 0 },
  metadata: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM, margin: 0,
  },
  metric: { minWidth: 0, overflowWrap: 'anywhere', '& dt': { color: tokens.colorNeutralForeground2 }, '& dd': { margin: 0 } },
  helper: { color: tokens.colorNeutralForeground2 },
  timeline: { margin: 0, paddingLeft: tokens.spacingHorizontalXL, '& li': { marginBottom: tokens.spacingVerticalM } },
  candidate: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalM },
  code: { fontFamily: tokens.fontFamilyMonospace },
});

export function RecordedTime({ value }: { value: string }) {
  const date = new Date(value);
  return <time dateTime={value}>{Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString()}</time>;
}

export function RoutingDecisionDetails({ record }: { record: RoutingDecision }) {
  const styles = useStyles();
  const outcome = outcomeExplanation(record.outcome);
  return (
    <>
      <Text as="h2" size={500} weight="semibold" className={styles.heading}>Recorded decision details</Text>
      <Badge appearance="tint" color={outcome.color}>{outcome.label}</Badge>
      <Text>{outcome.detail}</Text>
      {record.truncated && <MessageBar intent="warning"><MessageBarBody>This record was truncated. Missing evaluations or attempts cannot be inferred.</MessageBarBody></MessageBar>}
      <dl className={styles.metadata}>
        <div className={styles.metric}><dt>Record ID</dt><dd className={styles.code}>{record.id}</dd></div>
        {record.callId && <div className={styles.metric}><dt>Routing call ID</dt><dd className={styles.code}>{record.callId}</dd></div>}
        <div className={styles.metric}><dt>Trace ID</dt><dd className={styles.code}>{record.traceId ?? 'Not recorded'}</dd></div>
        <div className={styles.metric}><dt>Span ID</dt><dd className={styles.code}>{record.spanId ?? 'Not recorded'}</dd></div>
        <div className={styles.metric}><dt>Started</dt><dd><RecordedTime value={record.startedAtUtc} /></dd></div>
        <div className={styles.metric}><dt>Agent</dt><dd>{record.agentRef || 'Unspecified'}</dd></div>
        <div className={styles.metric}><dt>Requested model</dt><dd>{record.requestedModel || 'Unspecified'}</dd></div>
        <div className={styles.metric}><dt>Estimated tokens (not usage)</dt><dd>{record.estimatedTokens.toLocaleString()}</dd></div>
        <div className={styles.metric}><dt>Discovery at decision time</dt><dd>{record.discoveryEnabled ? 'Enabled' : 'Disabled'}</dd></div>
        <div className={styles.metric}><dt>Selection duration (excludes attempt)</dt><dd>{record.elapsedMilliseconds.toLocaleString()} ms</dd></div>
      </dl>
      <Accordion multiple collapsible defaultOpenItems={['candidates', 'attempt']}>
        <AccordionItem value="candidates">
          <AccordionHeader>Evaluated candidates ({record.candidates.length})</AccordionHeader>
          <AccordionPanel>
            <Text className={styles.helper}>Recorded order only. Unvisited and ineligible endpoints are omitted; absence is not a rejection.</Text>
            {record.candidates.length === 0 ? <p>No candidate evaluations were recorded.</p> : (
              <Accordion multiple collapsible>
                {record.candidates.map((candidate, index) => {
                  const reason = candidateExplanation(candidate.reason);
                  return (
                    <AccordionItem value={String(index)} key={index}>
                      <AccordionHeader>{index + 1}. {candidate.endpointId} — {reason.label}</AccordionHeader>
                      <AccordionPanel>
                        <div className={styles.candidate}>
                          <Badge color={reason.color} appearance="tint">{reason.label}</Badge>
                          <Text>{reason.detail}</Text>
                          <dl className={styles.metadata}>
                            <div className={styles.metric}><dt>Underlying model</dt><dd>{candidate.modelName || 'Not recorded'}</dd></div>
                            <div className={styles.metric}><dt>Wire deployment</dt><dd>{candidate.deployment || 'Not recorded'}</dd></div>
                            <div className={styles.metric}><dt>Priority</dt><dd>{candidate.priority}</dd></div>
                            <div className={styles.metric}><dt>Quota TPM</dt><dd>{candidate.quotaTpm === 0 ? 'Unknown / unmetered' : candidate.quotaTpm.toLocaleString()}</dd></div>
                            <div className={styles.metric}><dt>Panic mode</dt><dd>{candidate.panic ? 'Yes' : 'No'}</dd></div>
                            <div className={styles.metric}><dt>Spilled from tier</dt><dd>{candidate.spilledFromTier ?? 'Not recorded'}</dd></div>
                          </dl>
                        </div>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem value="attempt">
          <AccordionHeader>Attempt timeline</AccordionHeader>
          <AccordionPanel>
            <ol className={styles.timeline}>
              <li>Decision started: <RecordedTime value={record.startedAtUtc} /></li>
              <li>Recorded selection outcome: {outcome.label}. Selection duration: {record.elapsedMilliseconds.toLocaleString()} ms.</li>
              {record.attempt ? (
                <li>
                  <strong>{attemptKindLabel(record.attempt.kind)}: {outcomeExplanation(record.attempt.outcome).label}</strong>
                  <p>{outcomeExplanation(record.attempt.outcome).detail}</p>
                  <Text>Recorded attempt duration: {record.attempt.elapsedMilliseconds.toLocaleString()} ms.
                    HTTP status: {record.attempt.statusCode ?? 'Not recorded'}.</Text>
                </li>
              ) : <li>No attempt was recorded. This is not evidence that an endpoint was called or that a call succeeded.</li>}
            </ol>
            <Text className={styles.helper}>Durations are recorded measurements, not inferred start offsets. First-token acceptance is not full-stream completion.
              Retries appear as separate selection records with the same routing call ID when captured. Missing attempt updates may be pending or dropped.</Text>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
}
